import { useEffect, useState } from "react";
import { MaintenancePage } from "@/pages/MaintenancePage";
import { useAuth } from "@/state/AuthContext";
import { hasPermission } from "@/utils/permissions";
import { API_BASE_URL } from "@/utils/api";

interface MaintenanceStatus {
  active: boolean;
  message: string;
}

export function MaintenanceBanner() {
  const [status, setStatus] = useState<MaintenanceStatus | null>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/maintenance-status`)
      .then((r) => r.json())
      .then((data: MaintenanceStatus) => {
        if (data.active) setStatus(data);
      })
      .catch(() => {});
  }, []);

  if (!status?.active) return null;

  return (
    <div className="bg-amber-500 text-amber-950 text-sm text-center py-2 px-4 font-medium">
      Maintenance mode is on — regular users cannot access the app.
    </div>
  );
}

export function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState<MaintenanceStatus | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/maintenance-status`)
      .then((r) => r.json())
      .then((data: MaintenanceStatus) => setStatus(data))
      .catch(() => setStatus(null))
      .finally(() => setChecking(false));
  }, []);

  if (loading || checking) {
    return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  }

  const canBypass =
    user?.isPlatformAdmin || hasPermission(user, "admin_maintenance_access");

  if (status?.active && !canBypass) {
    return <MaintenancePage message={status.message} />;
  }

  return (
    <>
      {status?.active && canBypass && <MaintenanceBanner />}
      {children}
    </>
  );
}
